import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Shield, Clock, Users, HardHat, Star } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import PageSEO from "@/components/seo/PageSEO";
import { organizationSchema, websiteSchema } from "@/components/seo/schemaHelpers";
import { COMPANY } from "@/lib/company";
import { API, fetchServices, fetchProjects, fetchTestimonials } from "@/lib/api";

const HERO_FALLBACK =
    "https://images.unsplash.com/photo-1541888086225-f54045f28c46?auto=format&fit=crop&w=2000&q=80";

const VALUES = [
    {
        icon: Shield,
        label: "Licensed & Insured",
        text: "Fully certified across UK construction standards with £10M project liability.",
    },
    {
        icon: Clock,
        label: "On-Time Delivery",
        text: "Programme discipline is a non-negotiable. 96% of projects delivered on or before target.",
    },
    {
        icon: Users,
        label: "Skilled Workforce",
        text: "Directly employed trades and site management — no revolving-door subcontracting.",
    },
    {
        icon: HardHat,
        label: "Safety First",
        text: "Zero RIDDOR incidents to date — safety is our first metric on every site.",
    },
];

export default function HomePage() {
    const [services, setServices] = useState([]);
    const [projects, setProjects] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [heroSrc, setHeroSrc] = useState("/brand/hero-v5.jpg?v=5");
    const [loading, setLoading] = useState(true);

    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 600], [0, 120]);
    const heroScale = useTransform(scrollY, [0, 600], [1, 1.08]);

    useEffect(() => {
        let mounted = true;
        Promise.all([fetchServices(), fetchProjects(), fetchTestimonials()])
            .then(([s, p, t]) => {
                if (!mounted) return;
                setServices(s);
                setProjects(p.slice(0, 4));
                setTestimonials(t);
            })
            .catch((e) => console.error("home fetch failed", e))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <div data-testid="home-page">
            <PageSEO
                title="EB Construction — Manchester Construction Company | Built on Trust. Designed for Life."
                description="EB Construction is a Manchester-based UK construction firm delivering residential, commercial, industrial and civil projects across the North West. Licensed UK Home Office sponsor. Founded 2023."
                canonicalPath="/"
                jsonLd={[organizationSchema(), websiteSchema()]}
            />
            {/* Hero */}
            <section
                className="relative isolate overflow-hidden bg-brand-charcoal text-brand-offwhite"
                data-testid="home-hero"
            >
                <motion.div
                    style={{ y: heroY, scale: heroScale }}
                    className="absolute inset-0 -z-10"
                >
                    <img
                        src={heroSrc}
                        onError={() => setHeroSrc(HERO_FALLBACK)}
                        alt="Dusk view of a modern construction project in Manchester with glowing warm interior lighting"
                        className="h-full w-full object-cover"
                        fetchpriority="high"
                        loading="eager"
                        decoding="async"
                    />
                    {/* Base bottom→top dim for copy legibility on the dusk photograph. */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(to top, rgba(20,24,33,0.45) 0%, rgba(20,24,33,0.15) 30%, rgba(20,24,33,0) 55%)",
                        }}
                    />
                    {/* Left-side darkening — the building glows on the right; darken the sky
                        on the left so the Fraunces italic accent word reads cleanly. */}
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/55 via-transparent to-transparent" />
                    <div className="grain-overlay absolute inset-0" />
                </motion.div>

                <div className="container-x relative flex min-h-[92vh] flex-col justify-end pb-24 pt-40">
                    <Reveal>
                        <SectionLabel index={0} label="Construction Services" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-6 font-display text-[13vw] font-semibold leading-[1.02] tracking-tight md:mt-8 md:text-[8vw] lg:text-[6.8vw]">
                            Building{" "}
                            <span className="accent-italic text-brand-gold">excellence.</span>
                            <br />
                            <span className="text-brand-offwhite/95">Elevating standards.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p
                            className="mt-6 max-w-xl font-body text-lg italic tracking-wide text-brand-gold-light md:text-xl"
                            data-testid="hero-tagline"
                        >
                            Built on Trust. Designed for Life.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <div className="mt-10 grid gap-10 md:grid-cols-12 md:gap-16">
                            <p className="max-w-xl font-body text-lg leading-relaxed text-brand-offwhite/80 md:col-span-6 md:text-xl">
                                ElevateBuild is a UK construction firm engineered
                                for consequence — commercial, residential, civil
                                and infrastructure projects delivered with the
                                programme, safety and craft that serious clients
                                demand.
                            </p>
                            <div className="flex flex-col items-start gap-4 md:col-span-6 md:flex-row md:items-center md:justify-end">
                                <Link
                                    to="/contact"
                                    className="btn-primary"
                                    data-testid="hero-cta-quote"
                                >
                                    Get a Quote
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                                <Link
                                    to="/portal"
                                    className="btn-ghost-dark"
                                    data-testid="hero-cta-training"
                                >
                                    Explore Training
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.4}>
                        <div className="mt-16 grid grid-cols-2 gap-y-8 border-t border-brand-linelight pt-8 md:grid-cols-4">
                            {[
                                { k: "Est. 2023", v: "Founded in Manchester" },
                                { k: "24", v: "Projects Delivered" },
                                { k: "£46M", v: "Contract Value" },
                                { k: "Zero", v: "RIDDOR Incidents" },
                            ].map((s) => (
                                <div key={s.v} data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <p className="font-industrial text-5xl text-brand-gold md:text-6xl">
                                        {s.k}
                                    </p>
                                    <p className="mt-1 font-mono-tag text-brand-offwhite/70">
                                        {s.v}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Services overview */}
            <section
                className="border-b border-brand-line bg-brand-cream py-24 md:py-32"
                data-testid="home-services"
            >
                <div className="container-x">
                    <div className="grid gap-10 md:grid-cols-12">
                        <div className="md:col-span-5">
                            <Reveal>
                                <SectionLabel index={1} label="Services" />
                                <span className="rule-gold" aria-hidden="true" />
                                <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                                    Four disciplines.<br />
                                    <span className="accent-italic text-brand-gold">
                                        One standard.
                                    </span>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-6 md:col-start-7">
                            <Reveal delay={0.1}>
                                <p className="font-body text-lg leading-relaxed text-brand-steel">
                                    From ground-up commercial builds to
                                    infrastructure at civil scale, our teams work
                                    to the same standard: engineered discipline,
                                    zero-compromise safety, and a delivery model
                                    built around your programme.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="mt-16 grid grid-cols-1 border-t border-l border-brand-line md:grid-cols-2">
                        {services.map((s, i) => {
                            const Icon = LucideIcons[s.icon] || HardHat;
                            return (
                                <Reveal
                                    key={s.id}
                                    delay={i * 0.08}
                                    className="group relative border-b border-r border-brand-line bg-brand-paper p-8 transition-colors hover:bg-brand-offwhite md:p-12"
                                >
                                    <div
                                        className="flex items-start justify-between"
                                        data-testid={`service-card-${s.slug}`}
                                    >
                                        <div className="grid h-14 w-14 place-items-center border border-brand-charcoal text-brand-charcoal transition-colors group-hover:border-brand-gold group-hover:bg-brand-gold group-hover:text-brand-charcoal">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <span className="font-mono-tag text-brand-steel">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <h3 className="mt-8 font-display text-3xl font-semibold leading-[1.15] md:text-4xl">
                                        {s.title}
                                    </h3>
                                    <p className="mt-4 font-body text-base leading-relaxed text-brand-steel">
                                        {s.tagline}
                                    </p>
                                    <Link
                                        to="/services"
                                        className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal transition-colors group-hover:text-brand-gold"
                                        data-testid={`service-card-link-${s.slug}`}
                                    >
                                        Explore
                                        <ArrowUpRight className="h-3 w-3" />
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured Projects */}
            <section
                className="bg-brand-charcoal py-24 text-brand-offwhite md:py-32"
                data-testid="home-featured-projects"
            >
                <div className="container-x">
                    <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
                        <div>
                            <Reveal>
                                <SectionLabel index={2} label="Featured Work" tone="light" />
                                <span className="rule-gold" aria-hidden="true" />
                                <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                                    Selected <span className="accent-italic text-brand-gold">projects</span>
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <Link
                                to="/projects"
                                className="btn-ghost-dark"
                                data-testid="home-view-all-projects"
                            >
                                View All
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
                        {projects.map((p, i) => (
                            <Reveal
                                key={p.id}
                                delay={i * 0.08}
                                className={
                                    i % 4 === 0 || i % 4 === 3
                                        ? "md:col-span-7"
                                        : "md:col-span-5"
                                }
                            >
                                <Link
                                    to={`/projects/${p.slug}`}
                                    className="group block"
                                    data-testid={`featured-project-${p.slug}`}
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden md:aspect-[16/10]">
                                        <img
                                            src={p.image}
                                            alt={p.title}
                                            className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/60 to-transparent p-6">
                                            <p className="font-mono-tag text-brand-gold">
                                                {p.category} · {p.year}
                                            </p>
                                            <h3 className="mt-2 font-display text-2xl font-semibold leading-[1.15] md:text-3xl">
                                                {p.title}
                                            </h3>
                                            <p className="mt-1 font-body text-sm text-brand-offwhite/80">
                                                {p.location}
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why choose us */}
            <section
                className="border-b border-brand-line py-24 md:py-32"
                data-testid="home-why-us"
            >
                <div className="container-x">
                    <div className="grid gap-10 md:grid-cols-12">
                        <div className="md:col-span-4">
                            <Reveal>
                                <SectionLabel index={3} label="Why ElevateBuild" />
                                <span className="rule-gold" aria-hidden="true" />
                                <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-5xl">
                                    The difference is{" "}
                                    <span className="accent-italic text-brand-gold">discipline.</span>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8">
                            <div className="grid grid-cols-1 border-t border-l border-brand-line md:grid-cols-2">
                                {VALUES.map((v, i) => {
                                    const Icon = v.icon;
                                    return (
                                        <Reveal
                                            key={v.label}
                                            delay={i * 0.08}
                                            className="border-b border-r border-brand-line p-8"
                                        >
                                            <div
                                                className="flex items-start gap-4"
                                                data-testid={`value-prop-${v.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                            >
                                                <div className="grid h-12 w-12 flex-shrink-0 place-items-center bg-brand-gold text-brand-charcoal">
                                                    <Icon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <h3 className="font-display text-xl font-semibold leading-[1.2] md:text-2xl">
                                                        {v.label}
                                                    </h3>
                                                    <p className="mt-2 font-body text-sm leading-relaxed text-brand-steel">
                                                        {v.text}
                                                    </p>
                                                </div>
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials strip */}
            <section
                className="bg-brand-cream py-24 md:py-32"
                data-testid="home-testimonials"
            >
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={4} label="Client Voices" />
                        <span className="rule-gold" aria-hidden="true" />
                        <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                            What our clients{" "}
                            <span className="accent-italic text-brand-gold">say.</span>
                        </h2>
                    </Reveal>

                    <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {testimonials.slice(0, 3).map((t, i) => (
                            <Reveal
                                key={t.id}
                                delay={i * 0.08}
                                className="flex flex-col justify-between border border-brand-line bg-brand-paper p-8"
                            >
                                <div data-testid={`testimonial-${t.id}`}>
                                    <div className="flex gap-1 text-brand-gold">
                                        {Array.from({ length: t.rating }).map((_, k) => (
                                            <Star key={k} className="h-4 w-4 fill-current" />
                                        ))}
                                    </div>
                                    <p className="accent-italic mt-6 text-lg leading-relaxed text-brand-charcoal">
                                        “{t.quote}”
                                    </p>
                                </div>
                                <div className="mt-8 flex items-center gap-4 border-t border-brand-line pt-6">
                                    <img
                                        src={t.avatar}
                                        alt={t.name}
                                        className="h-12 w-12 object-cover"
                                    />
                                    <div>
                                        <p className="font-display text-base font-semibold leading-tight">
                                            {t.name}
                                        </p>
                                        <p className="font-mono-tag text-brand-steel">
                                            {t.role} · {t.company}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section
                className="relative isolate overflow-hidden bg-brand-gold py-24 md:py-32"
                data-testid="home-final-cta"
            >
                <div className="container-x relative">
                    <Reveal>
                        <p className="font-mono-tag text-brand-charcoal">
                            NEXT STEP
                        </p>
                        <h2 className="mt-6 max-w-4xl font-display text-5xl font-semibold leading-[1.05] text-brand-charcoal md:text-7xl">
                            Have a project in mind?<br />
                            <span className="accent-italic text-brand-offwhite">
                                Let's talk.
                            </span>
                        </h2>
                        <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 border border-brand-charcoal bg-brand-charcoal px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-gold transition-colors hover:bg-transparent hover:text-brand-charcoal"
                                data-testid="cta-band-quote-button"
                            >
                                Request a Quote
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                            <a
                                href="tel:+441615550142"
                                className="font-body text-lg text-brand-charcoal underline-offset-4 hover:underline"
                                data-testid="cta-band-phone"
                            >
                                or call{" "}
                                <a
                                    href={COMPANY.phoneHref}
                                    className="text-brand-gold underline-offset-4 hover:underline"
                                >
                                    {COMPANY.phoneDisplay}
                                </a>
                            </a>
                        </div>
                    </Reveal>
                </div>
                <div className="pointer-events-none absolute -bottom-16 -right-10 select-none font-industrial text-[220px] leading-none text-brand-charcoal/10 md:text-[360px]">
                    BUILD
                </div>
            </section>
        </div>
    );
}
